import {recordRumError} from '../../datadogRum';
import {performanceObserverConfiguration} from '../../performanceObserverConfiguration';
import {DEFAULT_CRITICAL_WORKFLOW_PAIN_WINDOW_DURATION_MS} from './constants';

/** Shares cooldowns between timed windows and caller-stopped sessions of the same workflow. */
export function createWorkflowSamplingPolicy() {
  const lastStarts = new Map<string, number>();

  const reportedUnknownWorkflows = new Set<string>();

  // Use current settings when deciding whether to start. Each running measurement
  // keeps the settings it started with, even if the config changes.
  function settings(workflow: string) {
    const config = performanceObserverConfiguration.getState();
    switch (workflow) {
      case 'route-changed':
        return {
          enabled: config.instrumentNavigation,
          minIntervalMs: config.minIntervalNavigation * 1000,
          windowDurationMs: config.windowNavigation * 1000,
        };
      case 'row-visibility-toggled':
      case 'edit-panel-x-metric-selection-changed':
      case 'edit-panel-y-metric-selection-changed':
      case 'line-plot-hovered':
      case 'run-search':
      case 'panel-search':
      case 'run-selector-pagination':
      case 'workspace-section-pagination':
      case 'workspace-section-opened':
      case 'workspace-section-closed':
        break;
      default:
        // Use workflow defaults for names missing from this switch. Log each missing
        // name once so repeated checks and user actions do not flood RUM.
        if (!reportedUnknownWorkflows.has(workflow)) {
          reportedUnknownWorkflows.add(workflow);
          recordRumError(
            'Workflow sampling policy fell through to the default rule',
            {workflow, fallbackPolicy: 'critical-workflow'}
          );
        }
    }
    return {
      enabled: config.instrumentWorkflows,
      minIntervalMs: config.minIntervalWorkflows * 1000,
      windowDurationMs: undefined,
    };
  }

  function isEnabled(workflow: string): boolean {
    return settings(workflow).enabled;
  }

  function minIntervalMs(workflow: string): number {
    return settings(workflow).minIntervalMs;
  }

  /** Navigation uses the configured duration; other workflows use the caller's duration or the default. */
  function windowDurationMs(
    workflow: string,
    requestedDurationMs?: number
  ): number {
    return (
      settings(workflow).windowDurationMs ??
      requestedDurationMs ??
      DEFAULT_CRITICAL_WORKFLOW_PAIN_WINDOW_DURATION_MS
    );
  }

  /** Checks enablement and cooldown without starting the cooldown. Pass performance.now() as now. */
  function canStart(workflow: string, now: number): boolean {
    return (
      isEnabled(workflow) &&
      now - (lastStarts.get(workflow) ?? -Infinity) >= minIntervalMs(workflow)
    );
  }

  /** Starts the cooldown after measurement setup succeeds; failed attempts must remain retryable. */
  function recordStart(workflow: string, now: number): void {
    lastStarts.set(workflow, now);
  }
  return {isEnabled, minIntervalMs, windowDurationMs, canStart, recordStart};
}
